export function toNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function calculateEmergencyFund({
  monthlySpend,
  currentSavings,
  targetMonths,
  monthlyContribution,
}) {
  const spend = Math.max(0, toNumber(monthlySpend));
  const savings = Math.max(0, toNumber(currentSavings));
  const target = Math.max(0, toNumber(targetMonths));
  const contribution = Math.max(0, toNumber(monthlyContribution));
  const targetAmount = spend * target;
  const gap = Math.max(0, targetAmount - savings);
  const surplus = Math.max(0, savings - targetAmount);

  return {
    monthsCovered: spend > 0 ? savings / spend : 0,
    targetAmount,
    gap,
    surplus,
    surplusMonths: spend > 0 ? surplus / spend : 0,
    isAtOrAboveTarget: gap === 0,
    monthsToTarget: gap === 0 ? 0 : (contribution > 0 ? Math.ceil(gap / contribution) : null),
  };
}

export function calculateDebtPayoff({ debts, extraPayment }) {
  const normalizedDebts = (debts || []).map((debt) => ({
    name: debt.name,
    balance: Math.max(0, toNumber(debt.balance)),
    apr: Math.max(0, toNumber(debt.apr)),
    minimumPayment: Math.max(0, toNumber(debt.minimumPayment ?? debt.minimum_payment)),
  }));
  const payoffOrder = [...normalizedDebts].sort((a, b) => b.apr - a.apr);
  const totalBalance = normalizedDebts.reduce((sum, debt) => sum + debt.balance, 0);
  const monthlyPayment = normalizedDebts.reduce((sum, debt) => sum + debt.minimumPayment, 0) + Math.max(0, toNumber(extraPayment));

  return {
    payoffOrder,
    totalBalance,
    estimatedMonths: monthlyPayment > 0 ? Math.ceil(totalBalance / monthlyPayment) : null,
    monthlyPayment,
  };
}

export function calculateDebtPayoffTimeline({ debts, extraPayment }) {
  const result = calculateDebtPayoff({ debts, extraPayment });
  if (!result.estimatedMonths) {
    return [{ label: 'Starting debt', month: 0, balance: result.totalBalance }];
  }

  const payoffMonths = result.estimatedMonths;
  const midpointMonth = Math.max(1, Math.floor(payoffMonths / 2));
  return [
    { label: 'Starting debt', month: 0, balance: result.totalBalance },
    {
      label: `Month ${midpointMonth}`,
      month: midpointMonth,
      balance: Math.max(0, result.totalBalance - result.monthlyPayment * midpointMonth),
    },
    { label: 'Projected payoff', month: payoffMonths, balance: 0 },
  ];
}

export function calculateRetirementContribution({
  annualIncome,
  currentContributionPct,
  proposedContributionPct,
  employerMatchPct,
  annualReturnPct,
  years,
}) {
  const income = Math.max(0, toNumber(annualIncome));
  const currentPct = Math.max(0, toNumber(currentContributionPct));
  const proposedPct = Math.max(0, toNumber(proposedContributionPct));
  const matchPct = Math.max(0, toNumber(employerMatchPct));
  const rate = Math.max(0, toNumber(annualReturnPct)) / 100;
  const horizon = Math.max(1, Math.floor(toNumber(years, 1)));
  const annualContributionIncrease = income * Math.max(0, proposedPct - currentPct) / 100;
  const monthlyPaycheckImpact = annualContributionIncrease / 12;
  const annualCurrent = income * Math.min(currentPct + matchPct, 100) / 100;
  const annualProposed = income * Math.min(proposedPct + matchPct, 100) / 100;

  const futureValue = (annualContribution) => {
    if (rate === 0) return annualContribution * horizon;
    return annualContribution * (((1 + rate) ** horizon - 1) / rate);
  };

  return {
    annualContributionIncrease,
    monthlyPaycheckImpact,
    annualCurrent,
    annualProposed,
    currentFutureValue: futureValue(annualCurrent),
    proposedFutureValue: futureValue(annualProposed),
    projectedUpside: futureValue(annualProposed) - futureValue(annualCurrent),
  };
}

export function calculateCompoundGrowth({
  initialAmount,
  monthlyContribution,
  annualReturnPct,
  years,
}) {
  const initial = Math.max(0, toNumber(initialAmount));
  const monthly = Math.max(0, toNumber(monthlyContribution));
  const annualRate = Math.max(0, toNumber(annualReturnPct)) / 100;
  const monthlyRate = annualRate / 12;
  const totalMonths = Math.max(1, Math.floor(toNumber(years, 1) * 12));
  const series = [];
  let balance = initial;

  for (let month = 0; month <= totalMonths; month += 1) {
    if (month > 0) {
      balance = balance * (1 + monthlyRate) + monthly;
    }
    if (month % 12 === 0 || month === totalMonths) {
      series.push({ year: Math.round(month / 12), balance });
    }
  }

  return {
    finalBalance: balance,
    contributions: initial + monthly * totalMonths,
    growth: Math.max(0, balance - initial - monthly * totalMonths),
    series,
  };
}

export function calculateRiskRange({ scenarios }) {
  const baseRanges = (scenarios || []).map((scenario) => {
    const expectedReturn = toNumber(scenario.expectedReturnPct ?? scenario.expected_return_pct);
    const volatility = Math.max(0, toNumber(scenario.volatilityPct ?? scenario.volatility_pct));
    return {
      label: scenario.label,
      expectedReturn,
      volatility,
      allocationPct: Math.max(0, toNumber(scenario.allocationPct ?? scenario.allocation_pct)),
      downside: expectedReturn - volatility,
      upside: expectedReturn + volatility,
      returnPerRisk: volatility > 0 ? expectedReturn / volatility : 0,
    };
  });
  const minDownside = Math.min(...baseRanges.map((range) => range.downside), 0);
  const maxUpside = Math.max(...baseRanges.map((range) => range.upside), 1);
  const span = Math.max(1, maxUpside - minDownside);

  return baseRanges.map((range) => ({
    ...range,
    rangeStartPct: Math.max(0, Math.min(100, ((range.downside - minDownside) / span) * 100)),
    rangeWidthPct: Math.max(4, Math.min(100, ((range.upside - range.downside) / span) * 100)),
    expectedPct: Math.max(0, Math.min(100, ((range.expectedReturn - minDownside) / span) * 100)),
  }));
}

export function calculateCashFlow({
  monthlyIncome,
  fixedExpenses,
  variableExpenses,
  savings,
  targetSavingsRatePct,
}) {
  const income = Math.max(0, toNumber(monthlyIncome));
  const fixed = Math.max(0, toNumber(fixedExpenses));
  const variable = Math.max(0, toNumber(variableExpenses));
  const saved = Math.max(0, toNumber(savings));
  const targetRate = Math.max(0, toNumber(targetSavingsRatePct));
  const monthlyOutflow = fixed + variable + saved;
  const monthlySurplus = income - monthlyOutflow;
  const targetSavingsAmount = income * targetRate / 100;

  return {
    monthlyOutflow,
    monthlySurplus,
    savingsRatePct: income > 0 ? (saved / income) * 100 : 0,
    targetSavingsAmount,
    savingsGap: Math.max(0, targetSavingsAmount - saved),
  };
}

export function monthsUntilTargetDate(targetDate, currentDate = new Date()) {
  const [year, month = 1] = String(targetDate || '').split('-').map((part) => Number(part));
  if (!Number.isFinite(year) || !Number.isFinite(month)) return null;

  const currentYear = currentDate.getUTCFullYear();
  const currentMonth = currentDate.getUTCMonth() + 1;
  return Math.max(0, (year - currentYear) * 12 + (month - currentMonth));
}

export function calculateGoalProgress({
  currentAmount,
  targetAmount,
  monthlyContribution,
  targetDate,
  currentDate,
}) {
  const current = Math.max(0, toNumber(currentAmount));
  const target = Math.max(0, toNumber(targetAmount));
  const contribution = Math.max(0, toNumber(monthlyContribution));
  const remaining = Math.max(0, target - current);
  const monthsToGoal = remaining === 0 ? 0 : (contribution > 0 ? Math.ceil(remaining / contribution) : null);
  const monthsAvailable = monthsUntilTargetDate(targetDate, currentDate);
  const requiredMonthly = monthsAvailable && monthsAvailable > 0 ? remaining / monthsAvailable : remaining;

  return {
    progressPct: target > 0 ? Math.min(100, (current / target) * 100) : 0,
    remaining,
    monthsToGoal,
    monthsAvailable,
    requiredMonthly,
    isOnPace: remaining === 0 || (monthsToGoal != null && monthsAvailable != null && monthsToGoal <= monthsAvailable),
  };
}

export function calculateMonthlyMortgage(principal, annualRatePct, years = 30) {
  const loan = Math.max(0, toNumber(principal));
  const monthlyRate = Math.max(0, toNumber(annualRatePct)) / 100 / 12;
  const payments = Math.max(1, Math.floor(toNumber(years, 30) * 12));
  if (monthlyRate === 0) return loan / payments;
  return loan * (monthlyRate * ((1 + monthlyRate) ** payments)) / (((1 + monthlyRate) ** payments) - 1);
}

export function calculateRentVsBuy({
  monthlyRent,
  homePrice,
  downPayment,
  mortgageRatePct,
  years,
  propertyTaxPct,
  maintenancePct,
  homeAppreciationPct,
  rentGrowthPct,
}) {
  const rent = Math.max(0, toNumber(monthlyRent));
  const price = Math.max(0, toNumber(homePrice));
  const down = Math.max(0, toNumber(downPayment));
  const horizon = Math.max(1, Math.floor(toNumber(years, 1)));
  const monthlyMortgage = calculateMonthlyMortgage(Math.max(0, price - down), mortgageRatePct, 30);
  let totalRent = 0;

  for (let year = 0; year < horizon; year += 1) {
    totalRent += rent * ((1 + Math.max(0, toNumber(rentGrowthPct)) / 100) ** year) * 12;
  }

  const ownershipCosts = (monthlyMortgage * 12 * horizon)
    + (price * Math.max(0, toNumber(propertyTaxPct)) / 100 * horizon)
    + (price * Math.max(0, toNumber(maintenancePct)) / 100 * horizon);
  const appreciatedValue = price * ((1 + Math.max(0, toNumber(homeAppreciationPct)) / 100) ** horizon);
  const equityGain = Math.max(0, appreciatedValue - price);
  const netBuyCost = Math.max(0, ownershipCosts - equityGain);

  return {
    totalRent,
    monthlyMortgage,
    ownershipCosts,
    appreciatedValue,
    equityGain,
    netBuyCost,
    buyAdvantage: totalRent - netBuyCost,
  };
}

export function calculatePortfolioDrift({ allocations }) {
  return (allocations || []).map((allocation) => {
    const currentPct = Math.max(0, toNumber(allocation.currentPct ?? allocation.current_pct));
    const targetPct = Math.max(0, toNumber(allocation.targetPct ?? allocation.target_pct));
    return {
      label: allocation.label,
      currentPct,
      targetPct,
      driftPct: currentPct - targetPct,
    };
  });
}

export function calculateTaxImpact({
  scenarioType,
  deductionAmount,
  marginalTaxRatePct,
  stateTaxRatePct,
  saleAmount,
  costBasis,
  capitalGainsTaxRatePct,
  extraIncome,
  conversionAmount,
}) {
  const type = scenarioType || 'deduction';
  const marginalRate = Math.max(0, toNumber(marginalTaxRatePct));
  const stateRate = Math.max(0, toNumber(stateTaxRatePct));
  const gainsRate = Math.max(0, toNumber(capitalGainsTaxRatePct));
  const totalTaxRatePct = type === 'capital_gain_sale' ? gainsRate + stateRate : marginalRate + stateRate;

  if (type === 'capital_gain_sale') {
    const sale = Math.max(0, toNumber(saleAmount));
    const taxableAmount = Math.max(0, sale - Math.max(0, toNumber(costBasis)));
    const estimatedTaxEffect = taxableAmount * totalTaxRatePct / 100;
    return {
      scenarioType: type,
      totalTaxRatePct,
      taxableAmount,
      estimatedTaxEffect,
      afterTaxResult: Math.max(0, sale - estimatedTaxEffect),
    };
  }

  if (type === 'ordinary_income') {
    const taxableAmount = Math.max(0, toNumber(extraIncome));
    const estimatedTaxEffect = taxableAmount * totalTaxRatePct / 100;
    return {
      scenarioType: type,
      totalTaxRatePct,
      taxableAmount,
      estimatedTaxEffect,
      afterTaxResult: Math.max(0, taxableAmount - estimatedTaxEffect),
    };
  }

  if (type === 'roth_conversion') {
    const taxableAmount = Math.max(0, toNumber(conversionAmount));
    const estimatedTaxEffect = taxableAmount * totalTaxRatePct / 100;
    return {
      scenarioType: type,
      totalTaxRatePct,
      taxableAmount,
      estimatedTaxEffect,
      afterTaxResult: estimatedTaxEffect,
    };
  }

  const taxableAmount = Math.max(0, toNumber(deductionAmount));
  const estimatedTaxEffect = -(taxableAmount * totalTaxRatePct / 100);
  return {
    scenarioType: 'deduction',
    totalTaxRatePct,
    taxableAmount,
    estimatedTaxEffect,
    afterTaxResult: Math.max(0, taxableAmount + estimatedTaxEffect),
  };
}

export function calculateInsuranceCoverage({
  annualIncome,
  yearsNeeded,
  existingCoverage,
  liquidAssets,
  debts,
  educationGoal,
}) {
  const incomeNeed = Math.max(0, toNumber(annualIncome)) * Math.max(0, toNumber(yearsNeeded));
  const obligations = Math.max(0, toNumber(debts)) + Math.max(0, toNumber(educationGoal));
  const offsets = Math.max(0, toNumber(existingCoverage)) + Math.max(0, toNumber(liquidAssets));
  const recommendedCoverage = Math.max(0, incomeNeed + obligations - Math.max(0, toNumber(liquidAssets)));

  return {
    incomeNeed,
    obligations,
    recommendedCoverage,
    coverageGap: Math.max(0, recommendedCoverage - Math.max(0, toNumber(existingCoverage))),
    coverageRatioPct: recommendedCoverage > 0 ? Math.min(100, (offsets / recommendedCoverage) * 100) : 100,
  };
}

export function calculateRetirementWithdrawal({
  portfolioValue,
  annualSpending,
  withdrawalRatePct,
  withdrawalMode,
  years,
  expectedReturnPct,
  volatilityPct,
  inflationPct,
  annualReturnPct,
}) {
  const startingValue = Math.max(0, toNumber(portfolioValue));
  const spending = Math.max(0, toNumber(annualSpending));
  const plannedRate = Math.max(0, toNumber(withdrawalRatePct));
  const horizon = Math.max(1, Math.floor(toNumber(years, 1)));
  const expectedReturn = Math.max(0, toNumber(expectedReturnPct ?? annualReturnPct, 5)) / 100;
  const volatility = Math.max(0, toNumber(volatilityPct, 12)) / 100;
  const inflation = Math.max(0, toNumber(inflationPct, 2)) / 100;
  const mode = withdrawalMode || 'fixed_amount';
  const endingBalances = [];
  const pathCount = 80;

  for (let path = 0; path < pathCount; path += 1) {
    let balance = startingValue;
    let currentSpending = spending;
    for (let year = 0; year < horizon; year += 1) {
      const withdrawal = mode === 'percent_of_portfolio' ? balance * plannedRate / 100 : currentSpending;
      balance = Math.max(0, balance - withdrawal);
      const cycle = Math.sin((path + 1) * (year + 1) * 12.9898) * 43758.5453;
      const pseudoRandom = cycle - Math.floor(cycle);
      const returnShock = (pseudoRandom - 0.5) * 1.3 * volatility;
      balance = Math.max(0, balance * (1 + expectedReturn + returnShock));
      currentSpending *= (1 + inflation);
    }
    endingBalances.push(balance);
  }

  const sorted = [...endingBalances].sort((a, b) => a - b);
  const successful = endingBalances.filter((balance) => balance > 0).length;
  const medianEndingBalance = sorted[Math.floor(sorted.length / 2)] || 0;
  const poorMarketEndingBalance = sorted[Math.floor(sorted.length * 0.1)] || 0;

  return {
    actualWithdrawalRatePct: startingValue > 0 ? (spending / startingValue) * 100 : 0,
    plannedWithdrawalAmount: startingValue * plannedRate / 100,
    successProbabilityPct: (successful / endingBalances.length) * 100,
    medianEndingBalance,
    poorMarketEndingBalance,
    projectedEndingBalance: medianEndingBalance,
    isAbovePlannedRate: startingValue > 0 ? (spending / startingValue) * 100 > plannedRate : false,
  };
}
